import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Warning, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders a "Pilot Mode Active" banner across the top of any workflow page
 * when the current user's primary property has Pilot Mode enabled.
 *
 * The banner is hidden if pilot mode is OFF or if the user is not tied to a property.
 * It listens for the global `pilot-mode-changed` event so the banner reflects
 * toggle changes from the Settings page without a refresh.
 */
const PilotModeBanner = () => {
  const { user, token } = useAuth();
  const propertyId = user?.property_ids?.[0];

  const [state, setState] = useState({ enabled: false, department: null });

  const fetchState = useCallback(async () => {
    if (!propertyId || !token) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/property-settings/${propertyId}/pilot-mode`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setState({
        enabled: Boolean(res.data.pilot_enabled),
        department: res.data.pilot_department || null,
      });
    } catch (e) {
      // Silent — banner just won't render
      setState({ enabled: false, department: null });
    }
  }, [propertyId, token]);

  useEffect(() => {
    fetchState();
    const handler = (e) => {
      // Optimistic update from a local toggle
      if (e?.detail) {
        setState({ enabled: !!e.detail.enabled, department: e.detail.department || null });
      } else {
        fetchState();
      }
    };
    window.addEventListener('pilot-mode-changed', handler);
    return () => window.removeEventListener('pilot-mode-changed', handler);
  }, [fetchState]);

  if (!state.enabled || !state.department) return null;

  // Hotel Admin / PM gets a "Configure" link; others get a generic info note.
  const canConfigure = user && ['purchasing_manager', 'system_admin'].includes(user.role);

  return (
    <div
      className="rounded-xl border border-[#3D1D72]/30 bg-[#F0EBF8] px-4 py-3 flex items-start gap-3 mb-4"
      data-testid="pilot-mode-banner"
    >
      <div className="mt-0.5 text-[#3D1D72]">
        <Warning size={20} weight="fill" />
      </div>
      <div className="flex-1 text-sm text-[#3D1D72]">
        <div className="font-semibold">
          Pilot Mode Active — Only {state.department} is visible.
        </div>
        <p className="text-xs text-[#3D1D72]/80 mt-0.5">
          {canConfigure ? (
            <>
              All other departments are hidden across the platform.{' '}
              <Link to="/settings" className="underline font-medium inline-flex items-center gap-0.5" data-testid="pilot-banner-configure-link">
                Open Property Settings to disable
                <ArrowRight size={12} />
              </Link>
            </>
          ) : (
            <>All other departments are hidden across the platform. Contact your hotel admin to disable pilot mode.</>
          )}
        </p>
      </div>
    </div>
  );
};

export default PilotModeBanner;
